import { useEffect } from "react";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "../../../lib/server-query";
import { retainedQueryData } from "@sx/api-client";
import { Page, Skeleton } from "@sx/ui";

import { usePrincipalId } from "../../../lib/browser-state";
import { registerWorkspaceHeader, type WorkspaceNavItem } from "../../../lib/nav";
import { started } from "../../../lib/prefetch";
import { loadedSession } from "../../../lib/session";
import { WorkspaceGate } from "../../../shell/WorkspaceGate";
import { api } from "../../../workspaces/production/api";
import { floorGrantsQuery } from "../../../workspaces/production/grants-query";
import { SeatProvider } from "../../../workspaces/production/seat";

/* Robot operations' Units: every unit, and what each runs.
 *
 * The units are the floor's, so the grant is the floor's: the same factory door answer
 * Production reads, fetched beside every other loader and answered here once. */
export const Route = createFileRoute("/_console/operations")({
  loader: async ({ context }) => {
    const { api: loaderApi } = await import("../../../workspaces/production/api");
    const principalId = loadedSession(context.queryClient)?.principal.id;
    if (principalId === undefined) return;
    const ready = started(
      context.queryClient.ensureQueryData(floorGrantsQuery(principalId, loaderApi.grants)),
    );
    if (import.meta.env.SSR) await ready;
  },
  component: OperationsLayout,
});

const OPERATIONS_NAV: ReadonlyArray<WorkspaceNavItem> = [
  { to: "/operations", label: "All units", icon: "robots", end: true },
  { to: "/operations/releases", label: "Releases", icon: "release", end: false },
];

function OperationsLayout() {
  const grants = useQuery(floorGrantsQuery(usePrincipalId(), api.grants));
  const role = retainedQueryData(grants);
  useEffect(
    () =>
      registerWorkspaceHeader({ workspace: "robot-fleet", items: OPERATIONS_NAV, presentation: "tabs" }),
    [],
  );
  if (role === undefined)
    return (
      <WorkspaceGate
        workspace="robot-fleet"
        query={grants}
        pending={
          <Page layout="page" role="status" aria-label="Loading the units">
            <Skeleton className="h-40 w-full" />
          </Page>
        }
      />
    );
  return (
    <SeatProvider seat={role}>
      <Outlet />
    </SeatProvider>
  );
}
